// Replenishment > Promoções — cliente da API interna (sessão).
import axios from 'axios';

const BASE_URL = '/api/replenishment/promotions';

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
  withCredentials: true,
});

// Sessão expirada -> login (mesmo comportamento do resto da app)
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    return Promise.reject(error);
  }
);

export type PromoAccao =
  | 'd2_alta' | 'd2_media' | 'ja_d2_reforcar' | 'nao_repor_t000' | 'manter_d1'
  | 'ja_d2_ok' | 'volumoso_d1' | 'sem_stock_t000' | 'fora_universo' | 'a_terminar';

export type PromoEstado = 'a_comecar' | 'a_decorrer';

export interface PromoOpportunity {
  snapshot_date: string;
  sku: string;
  salesdeal: string | null;
  data_inicio: string;
  data_fim: string;
  dias_promo: number;
  condicao_promo: string | null;
  preco_actual: number;
  preco_promo: number;
  desconto: number; // negativo (-0.33)
  nome: string | null;
  cat1: string | null;
  cat2: string | null;
  estado: string | null;
  canal_v3: string | null;
  cod_distribuicao_v3: string | null;
  motivo_v3: string | null;
  dpd28: number;
  q365: number | null;
  dias_hist: number;
  dpd_previsto: number;
  base_previsao: 'historico' | 'prior';
  confianca: 'alta' | 'baixa';
  share_lojas: number;
  un_lojas_promo: number;
  procura_online_promo: number;
  envio_lojas: number;
  t_chega: boolean;
  necessidade_promo: number;
  falta_promo: number;
  alerta_compras: boolean;
  lojas_com_stock: number | null;
  stock_por_loja: string | null;
  stock_lojas: number | null;
  stock_T: number | null;
  compra_T: number | null;
  accao: PromoAccao;
  accao_pt: string;
  accao_desejada: PromoAccao | null;
  prioridade: number;
  aplicar_ate: string;
  recepcao_loja: string;
  atrasado: boolean;
  calculated_at: string;
  // 02/11/2026 — NULL nos snapshots anteriores (= a começar)
  estado_promo?: PromoEstado | null;
  inicio_promo?: string | null; // início da promoção (pode ser anterior a data_inicio numa descida adicional)
  dias_decorridos?: number | null;
  q_promo_decorrida?: number | null;
  dpd_promo_real?: number | null;
  dias_restantes_loja?: number | null;
}

export interface PromoGroup {
  data_inicio: string;
  salesdeal: string | null;
  estado_promo?: PromoEstado | null;
  data_fim_min: string;
  data_fim_max: string;
  aplicar_ate: string;
  recepcao_loja: string;
  atrasado: boolean;
  n: number;
  a_repor: number;
  nao_repor: number;
  alerta_compras: number;
  falta_un: number | null;
}

export interface PromoList {
  snapshot: string | null;
  count: number;
  limit: number;
  offset: number;
  results: PromoOpportunity[];
  counts_accao: Record<string, number>;
  groups: PromoGroup[];
  totals: { n?: number; skus?: number; salesdeals?: number; alerta_compras?: number; aplicar_ate_min?: string | null;
    a_decorrer?: number; a_comecar?: number };
  calculated_at: string | null;
}

export interface PromoMeta {
  snapshots: Array<{ snapshot_date: string; n: number; calculated_at?: string | null }>;
  latest_snapshot: string | null;
  cat2: string[];
  salesdeals: Array<{ salesdeal: string | null; data_inicio: string; n: number }>;
  accoes: Array<{ value: PromoAccao; label: string }>;
  orderings: string[];
}

export interface PromoDetail {
  snapshot: string;
  sku: string;
  promotions: PromoOpportunity[];
  history: Array<{
    snapshot_date: string; data_inicio: string; salesdeal: string | null; accao: PromoAccao;
    dpd_previsto: number; stock_T: number | null; t_chega: boolean; falta_promo: number;
  }>;
}

export interface PromoFilters {
  snapshot?: string;
  start_from?: string;
  start_to?: string;
  salesdeal?: string;
  accao?: string;
  estado_promo?: string;
  cat2?: string;
  search?: string;
  apply_by?: string;
  alerta_compras?: string;
  so_atrasadas?: string;
  ordering?: string;
}

const clean = (filters: object) =>
  Object.fromEntries(Object.entries(filters as Record<string, unknown>).filter(([, v]) => v !== undefined && v !== ''));

export const exportUrl = (filters: PromoFilters): string => {
  const qs = new URLSearchParams(clean(filters) as Record<string, string>).toString();
  return `${BASE_URL}/export/${qs ? `?${qs}` : ''}`;
};

export const replenishmentPromotionsApi = {
  list: async (filters: PromoFilters, limit: number, offset: number): Promise<PromoList> =>
    (await client.get('/', { params: clean({ ...filters, limit, offset }) })).data,

  getMeta: async (): Promise<PromoMeta> => (await client.get('/meta/')).data,

  getDetail: async (sku: string, snapshot?: string): Promise<PromoDetail> =>
    (await client.get(`/${encodeURIComponent(sku)}/`, { params: clean({ snapshot }) })).data,
};
